/**
 * High-level chat actions: sendMessage, sendToolResult, loadHistory, etc.
 *
 * Preserves exact behavior from useChat.ts:
 * - sendMessage: user+assistant placeholder → startRun (lines 342-371)
 * - sendToolResult: atomic tool flip + placeholder → startRun with ToolMessage (lines 547-582)
 * - loadHistory: GET messages + state → attachToSession (lines 435-487)
 * - updateState: local top-level key mutator (lines 504-511)
 */

import { useCallback, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { AppState, AgUiTool } from "@strands/agent-transport";
import type { MessageInfo, Part } from "../types";
import { apiFetch } from "@/shared/api";
import { useChatStoreApi } from "../stores/chat-store-context";
import { useAgentStream } from "./use-agent-stream";
import { useSessionAttach } from "./use-session-attach";


// "Don't load history for this freshly-created session" — set by sendMessage
// on the create-and-send path. MUST be module-level, not a per-hook useRef:
// sendMessage navigates `/new → /?sid=`, which are different route components,
// so React unmounts one ChatPage and mounts another. A per-instance ref would
// reset to false on that remount, the second loadHistory (from the new
// instance's activeId effect) would NOT skip, and it would wipe the live
// streaming session — empty hero for the whole run, content only appearing
// after the run when history reloads. Module scope survives the remount.
// Cleared when the user actually leaves this session (see clearSkipLoad).
let skipLoadForSid: string | null = null;

/** Drop the skip-load flag once the user navigates to a different session
 *  (or hero), so returning to that session later reloads from history. */
export function clearSkipLoad(activeId: string | null): void {
  if (skipLoadForSid !== null && skipLoadForSid !== activeId) {
    skipLoadForSid = null;
  }
}

interface UseChatActionsOptions {
  onTitleUpdate?: (sessionId: string, title: string) => void;
  onSessionLoaded?: (state: AppState) => void;
}

export function useChatActions(options: UseChatActionsOptions = {}) {
  const storeApi = useChatStoreApi();
  const navigate = useNavigate();
  const { startRun, stopStreaming } = useAgentStream();
  const { attachToSession } = useSessionAttach();

  const onTitleUpdateRef = useRef(options.onTitleUpdate);
  onTitleUpdateRef.current = options.onTitleUpdate;
  const onSessionLoadedRef = useRef(options.onSessionLoaded);
  onSessionLoadedRef.current = options.onSessionLoaded;

  /**
   * Load message history + app state for a session, then attempt attach.
   */
  const loadHistory = useCallback(
    async (sessionId: string) => {
      // Freshly created + sent session: the live POST /runs stream is
      // authoritative and the store already holds the conversation. Skip
      // the reset+history-fetch for EVERY call while this is the target
      // (survives the /new→/?sid= remount; do NOT clear here).
      if (skipLoadForSid === sessionId) return;

      // Reset session state
      storeApi.setState((s) => {
        s.messages = [];
        s.partsMap = {};
        s.streaming = false;
        s.appState = {};
        s.inReasoning = false;
        s.activeSessionId = sessionId;
      });

      // Load messages
      try {
        const resp = await apiFetch(`/api/sessions/${sessionId}/messages`);
        const data: Array<{
          role: string;
          chunks: Array<{ type: string; content?: string; id?: string; name?: string; args?: Record<string, unknown>; status?: string; result?: string; error?: string | null }>;
          trace_id?: string;
          feedback?: { score: 1 | -1; comment?: string | null };
          command?: { label: string };
        }> = await resp.json();

        if (storeApi.getState().activeSessionId !== sessionId) return;

        // Convert chunk-based history to Part model
        const messages: MessageInfo[] = [];
        const partsMap: Record<string, Part[]> = {};

        for (const m of data) {
          const id = crypto.randomUUID();
          const info: MessageInfo = {
            id,
            role: m.role as "user" | "assistant",
            sessionId,
          };
          if (m.role === "assistant" && m.trace_id) {
            info.traceId = m.trace_id;
            if (m.feedback) info.feedback = m.feedback;
          }
          messages.push(info);
          const parts: Part[] = m.chunks.map((c): Part => {
            if (c.type === "text") return { type: "text", text: c.content ?? "" };
            if (c.type === "reasoning") return { type: "reasoning", text: c.content ?? "" };
            // tool_call
            return {
              type: "tool",
              id: c.id ?? "",
              tool: c.name ?? "",
              state: {
                status:
                  c.status === "executing"
                    ? "running"
                    : (c.status as "completed" | "error") ?? "completed",
                input: c.args ?? {},
                output: c.result,
                error: c.error,
              },
            };
          });
          // Re-attach the slash-command label so a reloaded command renders
          // as the compact chip instead of its expanded template.
          if (m.command) {
            const head = parts.find(
              (p): p is Extract<Part, { type: "text" }> => p.type === "text",
            );
            if (head) head.command = m.command;
          }
          partsMap[id] = parts;
        }

        storeApi.setState((s) => {
          s.messages = messages;
          s.partsMap = partsMap;
        });

        // Try attach to in-flight run
        await attachToSession(sessionId);
      } catch {
        /* ignore */
      }

      // Load state separately
      try {
        const resp = await apiFetch(`/api/sessions/${sessionId}/state`);
        const data: { state: AppState } = await resp.json();
        if (storeApi.getState().activeSessionId !== sessionId) return;
        const loaded = data.state || {};
        storeApi.setState((s) => {
          s.appState = loaded;
        });
        onSessionLoadedRef.current?.(loaded);
      } catch {
        if (storeApi.getState().activeSessionId === sessionId) {
          onSessionLoadedRef.current?.({});
        }
      }
    },
    [attachToSession, storeApi],
  );

  /**
   * Send a user message. Creates user + empty assistant placeholder,
   * then starts an agent run.
   */
  const sendMessage = useCallback(
    async (
      text: string,
      overrideSid?: string,
      command?: { label: string },
    ) => {
      const currentSid = storeApi.getState().activeSessionId;
      const targetSession = overrideSid ?? currentSid;
      if (!targetSession) return;

      // Mark skip for just-created sessions
      if (overrideSid && overrideSid !== currentSid) {
        skipLoadForSid = overrideSid;
        storeApi.setState((s) => {
          s.activeSessionId = overrideSid;
        });
      }

      const userId = crypto.randomUUID();
      const assistantId = crypto.randomUUID();

      storeApi.setState((s) => {
        s.messages.push(
          { id: userId, role: "user", sessionId: targetSession },
          { id: assistantId, role: "assistant", sessionId: targetSession },
        );
        s.partsMap[userId] = [{ type: "text", text, ...(command && { command }) }];
        s.partsMap[assistantId] = [];
      });

      // For just-created sessions, push the new sid into the URL so a refresh
      // mid-run lands back on this session (and attach replays the buffer).
      // Done AFTER the setState above — IndexRoute's render-time sync sees
      // store.activeSessionId already === overrideSid and stays a no-op,
      // so it won't trigger setActiveSession's reset that would wipe the
      // user+assistant messages we just pushed.
      if (overrideSid && overrideSid !== currentSid) {
        navigate({ to: "/", search: { sid: overrideSid } });
      }

      await startRun(
        targetSession,
        [{ id: crypto.randomUUID(), role: "user", content: text }],
        command ? { commandLabel: command.label } : undefined,
      );
    },
    [startRun, storeApi, navigate],
  );

  /**
   * HITL resume: post a ToolMessage to continue the agent loop.
   * Atomically flips the tool part to completed + appends assistant placeholder.
   */
  const sendToolResult = useCallback(
    async (toolCallId: string, content: string) => {
      const sessionId = storeApi.getState().activeSessionId;
      if (!sessionId) return;

      // Single atomic update: flip tool + push assistant placeholder
      storeApi.getState().completeToolAndPushAssistant(toolCallId, content, sessionId);

      await startRun(sessionId, [
        {
          id: crypto.randomUUID(),
          role: "tool",
          toolCallId,
          content,
        },
      ]);
    },
    [startRun, storeApi],
  );

  /**
   * Resume a Strands HITL interrupt. Pushes the user's decision through
   * `RunAgentInput.forwardedProps.interruptResponses`; the backend feeds
   * it to `agent([...])` so the paused tool call either fires or gets
   * cancelled. Empty `messages[]` is fine — the backend recognises
   * interrupt-resume from the forwardedProps and doesn't require a user
   * message. We push an assistant placeholder so the agent's
   * continuation has a bubble to stream into.
   */
  const respondToInterrupt = useCallback(
    async (interruptId: string, response: "A" | "D") => {
      const sessionId = storeApi.getState().activeSessionId;
      if (!sessionId) return;

      // Optimistic clear so the approval card unmounts instantly. The
      // backend eventually emits its own STATE_DELTA replacing
      // pending_approvals after the resumed agent settles — idempotent
      // if we've already dropped this entry.
      const assistantId = crypto.randomUUID();
      storeApi.setState((s) => {
        const pa = s.appState.pending_approvals as Record<string, unknown> | undefined;
        if (pa && interruptId in pa) {
          const next = { ...pa };
          delete next[interruptId];
          s.appState.pending_approvals = next;
        }
        s.messages.push({ id: assistantId, role: "assistant", sessionId });
        s.partsMap[assistantId] = [];
      });

      storeApi.getState().setRuntimeForwardedProps({
        interruptResponses: [{ interruptId, response }],
      });

      try {
        await startRun(sessionId, []);
      } finally {
        // Reset so the next user message doesn't accidentally carry a
        // stale interrupt response.
        storeApi.getState().setRuntimeForwardedProps({});
      }
    },
    [startRun, storeApi],
  );

  /**
   * Local top-level state mutator for client-owned keys.
   */
  const updateState = useCallback((key: string, value: unknown) => {
    storeApi.getState().updateAppStateKey(key, value);
  }, [storeApi]);

  /**
   * Register a client-declared tool.
   */
  const registerTool = useCallback((tool: AgUiTool): (() => void) => {
    return storeApi.getState().registerTool(tool);
  }, [storeApi]);

  return {
    sendMessage,
    sendToolResult,
    respondToInterrupt,
    stopStreaming,
    updateState,
    registerTool,
    loadHistory,
    onTitleUpdateRef,
  };
}
